import { Injectable, OnDestroy } from '@angular/core';
import { InstancesRegisterService } from '@cloud-twin-v2/generated/services/instances-register.service';
import {
  InstanceResponseDto,
  WorkspaceConnectSettingDto,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { ReplaySubject, Subscription } from 'rxjs';
import { ReportsService } from '../../../../../thin-client/src/app/master-operator/services/reports.service';
import { InstanceUpdatesWorkerLoaderService } from './instance-updates-worker-loader.service';
import {
  ReportAvailableWorkerResponse,
  ResultsPublishedWorkerResponse,
} from './instance-updates-worker-response';

@Injectable()
export class InstanceUpdatesManagerService implements OnDestroy {
  private _updatedInstance$ = new ReplaySubject<InstanceResponseDto>();
  readonly updatedInstance$ = this._updatedInstance$.asObservable();

  private _connectionId$ = new ReplaySubject<string>();
  readonly connectionId$ = this._connectionId$.asObservable();

  private _resultsPublished$ = new ReplaySubject<ResultsPublishedWorkerResponse>();
  readonly resultsPublished$ = this._resultsPublished$.asObservable();

  private _initialized$ = new ReplaySubject<void>(1);
  private _updatedSubs: Subscription;
  private _initializedSubs: Subscription;

  constructor(
    private readonly _workerService: InstanceUpdatesWorkerLoaderService,
    private readonly _instancesRegisterService: InstancesRegisterService,
    private readonly _reportsService: ReportsService
  ) {}

  start(): void {
    this._workerService.start();
    this.listenResponse();
  }

  stop(): void {
    this._workerService.stop();
  }

  registerInstancesFromSettings(connectSettings: WorkspaceConnectSettingDto[]): void {
    const instanceIds = this.getInstanceIds(connectSettings);
    instanceIds.forEach((instanceId) => this.registerInstance(instanceId));
  }

  registerWorkspacesFromSettings(connectSettings: WorkspaceConnectSettingDto[]): void {
    const ids = this.getWorkspaceIds(connectSettings);
    ids.forEach((id) => this.registerWorkspace(id));
  }

  unregisterInstancesFromSettings(connectSettings: WorkspaceConnectSettingDto[]): void {
    const instanceIds = this.getInstanceIds(connectSettings);
    instanceIds.forEach((instanceId) => this.unregisterInstance(instanceId));
  }

  unregisterWorkspacesFromSettings(connectSettings: WorkspaceConnectSettingDto[]): void {
    const ids = this.getWorkspaceIds(connectSettings);
    ids.forEach((id) => this.unregisterWorkspace(id));
  }

  getInstanceIds = (connectSettings: WorkspaceConnectSettingDto[]) => {
    const instanceIdsSet = new Set<string>();
    connectSettings.forEach((item) => instanceIdsSet.add(item.instanceId));
    return Array.from(instanceIdsSet.values());
  };

  getWorkspaceIds = (connectSettings: WorkspaceConnectSettingDto[]) => {
    const workspaceIdsSet = new Set<string>();
    connectSettings.forEach((item) => workspaceIdsSet.add(item.workspaceId));

    return Array.from(workspaceIdsSet.values());
  };

  /**
   * Notify the worker so it will emit a signalR event for registering an instance.
   */
  registerInstance(instanceId: string): void {
    this._initializedSubs = this._initialized$.subscribe(() => {
      this._workerService.sendParams({
        registerInstance: { instanceId },
      });
    });
  }

  registerWorkspace(workspaceId: string): void {
    this._initializedSubs = this._initialized$.subscribe(() => {
      this._workerService.sendParams({
        registerWorkspace: { workspaceId },
      });
    });
  }

  unregisterInstance(instanceId: string): void {
    this._initializedSubs = this._initialized$.subscribe(() => {
      this._workerService.sendParams({
        unregisterInstance: { instanceId },
      });
    });
  }

  unregisterWorkspace(workspaceId: string): void {
    this._initializedSubs = this._initialized$.subscribe(() => {
      this._workerService.sendParams({
        unregisterWorkspace: { workspaceId },
      });
    });
  }

  private listenResponse(): void {
    this._updatedSubs = this._workerService.response$.subscribe((response) => {
      if (response.isInitialized) {
        this._initialized$.next();
      } else if (response?.instanceId) {
        this.handleUpdatedInstance(response.instanceId);
      } else if (response?.reportAvailable) {
        this.handleReportAvailable(response.reportAvailable);
      } else if (response?.connectionId) {
        this._connectionId$.next(response.connectionId);
      } else if (response?.resultsPublished) {
        this.handleResultsPublished(response.resultsPublished);
      }
    });
  }

  private handleUpdatedInstance(instanceId: string): void {
    this._instancesRegisterService.getInstance(instanceId).subscribe((instance) => {
      this._updatedInstance$.next(instance);
    });
  }

  private handleReportAvailable(reportAvailable: ReportAvailableWorkerResponse): void {
    this._reportsService
      .getGeneratedReport(reportAvailable.instanceId, reportAvailable.fileName)
      .subscribe((fileResponse) => {
        globalUtilsHelper.downloadBlobFile(fileResponse, reportAvailable.fileName);
      });
  }

  private handleResultsPublished(resultsPublished: ResultsPublishedWorkerResponse) {
    this._resultsPublished$.next(resultsPublished);
  }

  ngOnDestroy(): void {
    this._updatedSubs?.unsubscribe();
    this._initializedSubs?.unsubscribe();
  }
}
